// Support roles — Phase 2. Full identity per role + extended bonus types.
//
// 30/05/2026 — i18n migration: every user-facing string carries an `*Key`
// alongside its FR source-of-truth. Consumers should look up the *Key via
// `t()` (see `data/supportRolesI18n.js`) and fall back to the FR field.
// File-level marker — the FR strings here are intentional data defaults.
/* i18n-keep-file */
//
// Bonus types (all consumed by their respective slices):
/* i18n-keep */
//   { type: 'revenue', value: 0.10 }                  → +10 % daily revenue
//   { type: 'fuel_discount', value: 0.05 }            → -5 % fuel cost
//   { type: 'maintenance_discount', value: 0.20 }     → -20 % maintenance cost
//   { type: 'tax_reduction', value: 0.03 }            → -3 % corporate tax
//   { type: 'agios_reduction', value: 0.10 }          → -10 % daily agios
//   { type: 'reputation_passive', value: 1 }          → +N reputation / 30 days
//   { type: 'notoriety_passive', value: 1 }           → +N notoriety / 30 days
//   { type: 'satisfaction_passive', value: 5 }        → +N drivers satisfaction / 30 days
//   { type: 'turnover_reduction', value: 0.50 }       → -50 % chance of random driver loss
//   { type: 'event_rep_mitigation', value: 0.50 }     → halve reputation drop from events
//   { type: 'unlock', value: 'market_intel' }         → enables feature flag
//
// Unlock flags consumed by the UI:
//   - 'market_intel'         (Marche page): show competitor pricing
//   - 'salons'               (Dashboard action): pay 1 000 € → +5 reputation
//   - 'grands_comptes'       (contract generation): unlocks high-value contracts
//   - 'contrats_cadres'      (Achats): allows bulk purchases at discount
//   - 'crisis_comm'          (Events): adds a "communication de crise" choice that softens rep penalty
//   - 'negociation_taux'     (Banque): -1 % on new loan rates
//   - 'diag_preventif'       (Events/Maintenance): halves random mechanical events frequency

export const SUPPORT_FAMILIES = [
  { id: 'admin', name: 'Administration / Exploitation', icon: 'ClipboardList' },
  { id: 'commerce', name: 'Commerce', icon: 'Briefcase' },
  { id: 'marketing', name: 'Marketing', icon: 'BarChart3' },
  { id: 'communication', name: 'Communication', icon: 'Megaphone' },
  { id: 'achats', name: 'Achats', icon: 'ShoppingCart' },
  { id: 'finance', name: 'Comptabilité / Finance', icon: 'Calculator' },
  { id: 'rh', name: 'Ressources Humaines', icon: 'Users' },
  { id: 'sav', name: 'SAV / Centre d\'appel', icon: 'Headphones' },
  { id: 'garage', name: 'Garage / Mécanicien', icon: 'Wrench' },
  // 01/06/2026 — QHSE family (Quality, Health, Safety, Environment / RSE).
  { id: 'qhse', name: 'QHSE / RSE', icon: 'ShieldCheck' },
  // 17/06/2026 (Lot 6 Phase L1) — Juridique / Conseil. Permet l'avocat-
  // conseil Juliette Charron-Berthet et le futur cabinet externe.
  { id: 'juridique', name: 'Juridique / Conseil', icon: 'Scale' },
];

export const SUPPORT_ROLES = [
  // ===== ADMINISTRATION / EXPLOITATION =====
  {
    id: 'role-nathalie-robert',
    familyId: 'admin',
    role: 'Assistante administrative',
    name: 'Nathalie Robert', age: 38,
    portrait: '/support-portraits/role-nathalie-robert.webp',
    level: 'Junior',
    background: 'Formation Gestion, méthodique et rigoureuse, soulage la direction sur tout le suivi administratif quotidien.',
    monthlyCost: 2414,
    prerequisite: { type: 'monthlyRevenue', value: 10000, label: 'CA > 10 000 €/mois' },
    bonuses: [
      { type: 'revenue', value: 0.10, label: '+10 % revenus (facturation fluidifiée)' },
    ],
    unlocks: [],
  },
  {
    id: 'role-christophe-faure',
    familyId: 'admin',
    role: "Responsable d'exploitation",
    name: 'Rémi Forni', age: 45,
    portrait: '/support-portraits/role-christophe-faure.webp',
    level: 'Senior',
    background: 'Cadre transport pendant 15 ans, pilote tournées et imprévus avec une rigueur militaire.',
    monthlyCost: 4260,
    prerequisite: { type: 'monthlyRevenue', value: 25000, label: 'CA > 25 000 €/mois' },
    bonuses: [
      { type: 'revenue', value: 0.20, label: '+20 % revenus (planification optimisée)' },
      { type: 'reputation_passive', value: 1, label: '+1 réputation / mois (fiabilité)' },
    ],
    unlocks: [],
  },

  // ===== COMMERCE =====
  {
    id: 'role-lea-bertrand',
    familyId: 'commerce',
    role: 'Commerciale junior',
    name: 'Léa Bertrand', age: 26,
    portrait: '/support-portraits/role-lea-bertrand.webp',
    level: 'Junior',
    background: 'Ex-grande distribution sportive, énergique et orientée terrain.',
    monthlyCost: 2200,
    prerequisite: { type: 'monthlyRevenue', value: 5000, label: 'CA > 5 000 €/mois' },
    bonuses: [
      { type: 'revenue', value: 0.10, label: '+10 % revenus contrats classiques' },
    ],
    unlocks: [],
  },
  {
    id: 'role-marc-dupuis',
    familyId: 'commerce',
    role: 'Commercial terrain',
    name: 'Marc Dupuis', age: 42,
    portrait: '/support-portraits/role-marc-dupuis.webp',
    level: 'Senior',
    background: '15 ans dans le transport, démarche prospects et participe aux salons régionaux.',
    monthlyCost: 3550,
    prerequisite: { type: 'monthlyRevenue', value: 20000, label: 'CA > 20 000 €/mois' },
    bonuses: [
      { type: 'revenue', value: 0.30, label: '+30 % revenus (nouveaux contrats apportés)' },
    ],
    unlocks: ['salons'],
  },
  {
    id: 'role-sylvie-marchand',
    familyId: 'commerce',
    role: 'Directrice commerciale',
    name: 'Sylvie Marchand', age: 51,
    portrait: '/support-portraits/role-sylvie-marchand.webp',
    level: 'Expert',
    background: 'Ancienne cadre logistique grands comptes, ouvre les portes de l\'industrie premium.',
    monthlyCost: 5800,
    prerequisite: { type: 'roleHired', value: 'role-marc-dupuis', label: 'Marc Dupuis recruté + CA > 50 000 €/mois' },
    extraPrerequisite: { type: 'monthlyRevenue', value: 50000 },
    bonuses: [
      { type: 'revenue', value: 0.50, label: '+50 % revenus' },
    ],
    unlocks: ['grands_comptes'],
  },

  // ===== MARKETING =====
  {
    id: 'role-yanis-elkaroui',
    familyId: 'marketing',
    role: 'Chargé marketing',
    name: 'Yanis El Karoui', age: 24,
    portrait: '/support-portraits/role-yanis-elkaroui.webp',
    level: 'Junior',
    background: 'École de communication, créatif et au fait des codes digitaux.',
    monthlyCost: 2400,
    prerequisite: { type: 'monthlyRevenue', value: 8000, label: 'CA > 8 000 €/mois' },
    bonuses: [
      { type: 'notoriety_passive', value: 1, label: '+1 notoriété / mois (présence digitale)' },
      { type: 'revenue', value: 0.05, label: '+5 % contrats (effet vitrine)' },
    ],
    unlocks: [],
  },
  // 09/02/2026 — Q2 Lot F user prod : remplacement de Camille Roussel par
  // Sherazade Fetitou (brune 40 ans, ex-chauffeuse N4 ADR/pharma qui pivote
  // vers le marketing). Le filename portrait est aussi renommé.
  {
    id: 'role-sherazade-fetitou',
    familyId: 'marketing',
    role: 'Responsable marketing',
    name: 'Sherazade Fetitou', age: 40,
    portrait: '/support-portraits/role-sherazade-fetitou.webp',
    level: 'Senior',
    background: 'Ex-chauffeuse longue distance reconvertie agence pub, structure une vraie stratégie de marque ancrée terrain.',
    monthlyCost: 4100,
    prerequisite: { type: 'roleHired', value: 'role-yanis-elkaroui', label: 'Yanis El Karoui recruté' },
    bonuses: [
      { type: 'notoriety_passive', value: 2, label: '+2 notoriété / mois' },
    ],
    unlocks: ['market_intel'],
  },

  // ===== COMMUNICATION =====
  {
    id: 'role-thomas-vidal',
    familyId: 'communication',
    role: 'Community Manager',
    name: 'Thomas Vidal', age: 28,
    portrait: '/support-portraits/role-thomas-vidal.webp',
    level: 'Junior',
    background: 'Freelance, gère les réseaux sociaux et fluidifie les premières crises en ligne.',
    monthlyCost: 2100,
    prerequisite: { type: 'monthlyRevenue', value: 6000, label: 'CA > 6 000 €/mois' },
    bonuses: [
      { type: 'revenue', value: 0.05, label: '+5 % contrats' },
      { type: 'event_rep_mitigation', value: 0.50, label: '-50 % impact aléas réputation' },
    ],
    unlocks: [],
  },
  {
    id: 'role-aicha-diallo',
    familyId: 'communication',
    role: 'Attachée de presse',
    name: 'Aïcha Diallo', age: 38,
    portrait: '/support-portraits/role-aicha-diallo.webp',
    level: 'Senior',
    background: 'Ex-attachée de presse, transforme chaque crise en opportunité narrative.',
    monthlyCost: 3700,
    prerequisite: { type: 'roleHired', value: 'role-yanis-elkaroui', label: 'Yanis El Karoui recruté' },
    bonuses: [
      { type: 'notoriety_passive', value: 1.5, label: '+1,5 notoriété / mois' },
      { type: 'reputation_passive', value: 0.5, label: '+0,5 réputation / mois (gestion de crise)' },
    ],
    unlocks: ['crisis_comm'],
  },

  // ===== ACHATS =====
  {
    id: 'role-karim-benali',
    familyId: 'achats',
    role: 'Acheteur junior',
    name: 'Karim Benali', age: 30,
    portrait: '/support-portraits/role-karim-benali.webp',
    level: 'Junior',
    background: 'Ex-acheteur chez un constructeur automobile, ferme et analytique.',
    monthlyCost: 2600,
    prerequisite: { type: 'vehicleCount', value: 3, label: '3+ véhicules' },
    bonuses: [
      { type: 'fuel_discount', value: 0.05, label: '-5 % coût carburant' },
    ],
    unlocks: [],
  },
  {
    id: 'role-helene-pichon',
    familyId: 'achats',
    role: 'Responsable achats',
    name: 'Hélène Pichon', age: 45,
    portrait: '/support-portraits/role-helene-pichon.webp',
    level: 'Senior',
    background: 'Acheteuse grands comptes industrie, négocie des conditions cadres très favorables.',
    monthlyCost: 4200,
    prerequisite: { type: 'roleHired', value: 'role-karim-benali', label: 'Karim Benali recruté + 8+ véhicules' },
    extraPrerequisite: { type: 'vehicleCount', value: 8 },
    bonuses: [
      { type: 'fuel_discount', value: 0.10, label: '-10 % carburant' },
      { type: 'maintenance_discount', value: 0.08, label: '-8 % coût maintenance' },
    ],
    unlocks: ['contrats_cadres'],
  },

  // ===== COMPTABILITÉ / FINANCE =====
  {
    id: 'role-sophie-lambert',
    familyId: 'finance',
    role: 'Comptable',
    name: 'Sophie Lambert', age: 32,
    portrait: '/support-portraits/role-sophie-lambert.webp',
    level: 'Junior',
    background: 'DCG en poche, méticuleuse, sait optimiser les déductions fiscales.',
    monthlyCost: 2800,
    prerequisite: { type: 'monthlyRevenue', value: 15000, label: 'CA > 15 000 €/mois' },
    bonuses: [
      { type: 'tax_reduction', value: 0.03, label: '-3 % IS (optimisation fiscale)' },
    ],
    unlocks: ['audit_fiscal'],
  },
  {
    id: 'role-pierre-marchetti',
    familyId: 'finance',
    role: "Directeur administratif et financier",
    name: 'Tristan Midal', age: 48,
    portrait: '/support-portraits/role-pierre-marchetti.webp',
    level: 'Senior',
    background: "Ex-DAF d'un groupe de transport, sait négocier avec les banques et anticiper l'IS.",
    monthlyCost: 5200,
    prerequisite: { type: 'roleHired', value: 'role-sophie-lambert', label: 'Sophie Lambert recrutée' },
    bonuses: [
      { type: 'tax_auto_provisioning', value: 1, label: 'Débloque le provisionnement auto TVA + 25 % IS par contrat livré (zéro mauvaise surprise au bilan)' },
      { type: 'agios_reduction', value: 0.10, label: '-10 % agios' },
    ],
    unlocks: ['negociation_taux', 'fiscal_simulator', 'tax_auto_provisioning'],
  },

  // ===== RH =====
  {
    id: 'role-nadia-cheikh',
    familyId: 'rh',
    role: 'Responsable RH',
    name: 'Nadia Cheikh', age: 36,
    portrait: '/support-portraits/role-nadia-cheikh.webp',
    level: 'Senior',
    background: 'Ex-cabinet de recrutement, fidélise les équipes et baisse drastiquement le turnover.',
    monthlyCost: 3300,
    // 28/07/2026 — demande user : Nadia recrutable sans prérequis.
    bonuses: [
      { type: 'turnover_reduction', value: 0.50, label: '-50 % turnover chauffeurs' },
      { type: 'satisfaction_passive', value: 5, label: '+5 satisfaction passive / mois' },
      { type: 'driver_pool_tier_1', value: 1, label: '+4 profils chauffeurs recrutables (tier 1)' },
      { type: 'hr_insights', value: 1, label: 'Révèle traits de personnalité quotidiens + matrice de compatibilité' },
    ],
    unlocks: ['hr_dashboard'],
  },
  {
    id: 'role-karim-sefrioui',
    familyId: 'rh',
    role: 'Chasseur de tête',
    name: 'Karim Sefrioui', age: 42,
    portrait: '/support-portraits/role-karim-sefrioui.webp',
    level: 'Senior',
    background: 'Tête chercheuse du transport — connaît les meilleurs profils du marché et débloque les talents premium.',
    monthlyCost: 4200,
    prerequisite: { type: 'roleHired', value: 'role-nadia-cheikh', label: 'Nadia Cheikh recrutée' },
    bonuses: [
      { type: 'driver_pool_tier_2', value: 1, label: '+4 profils chauffeurs premium (tier 2)' },
      { type: 'turnover_reduction', value: 0.20, label: '-20 % turnover supplémentaire' },
    ],
    unlocks: [],
  },

  // ===== SAV / CENTRE D'APPEL =====
  {
    id: 'role-lucas-mercier',
    familyId: 'sav',
    role: 'Hotliner',
    name: 'Lucas Mercier', age: 22,
    portrait: '/support-portraits/role-lucas-mercier.webp',
    level: 'Junior',
    background: 'Patient et clair au téléphone, gère les premières réclamations.',
    monthlyCost: 2000,
    prerequisite: { type: 'monthlyRevenue', value: 10000, label: 'CA > 10 000 €/mois' },
    bonuses: [
      { type: 'reputation_passive', value: 0.5, label: '+0,5 réputation / mois' },
    ],
    unlocks: [],
  },
  {
    id: 'role-ines-bouchard',
    familyId: 'sav',
    role: 'Responsable service client',
    name: 'Inès Bouchard', age: 29,
    portrait: '/support-portraits/role-ines-bouchard.webp',
    level: 'Senior',
    background: 'Structure un vrai service client, méthodique et empathique.',
    monthlyCost: 3200,
    prerequisite: { type: 'roleHired', value: 'role-lucas-mercier', label: 'Lucas Mercier recruté' },
    bonuses: [
      { type: 'reputation_passive', value: 1, label: '+1 réputation / mois' },
      { type: 'event_rep_mitigation', value: 0.50, label: '-50 % impact aléas réputation' },
    ],
    unlocks: [],
  },

  // ===== GARAGE / MÉCANICIEN =====
  {
    id: 'role-mehdi-ferhat',
    familyId: 'garage',
    role: 'Mécanicien',
    name: 'Mehdi Ferhat', age: 27,
    portrait: '/support-portraits/role-mehdi-ferhat.webp',
    level: 'Junior',
    background: 'CAP méca + 5 ans atelier, débrouillard et efficace.',
    monthlyCost: 2700,
    prerequisite: { type: 'vehicleCount', value: 4, label: '4+ véhicules' },
    bonuses: [
      { type: 'maintenance_discount', value: 0.20, label: '-20 % coût maintenance' },
    ],
    unlocks: [],
  },
  {
    id: 'role-bruno-lavergne',
    familyId: 'garage',
    role: "Chef d'atelier",
    name: 'Bruno Lavergne', age: 50,
    portrait: '/support-portraits/role-bruno-lavergne.webp',
    level: 'Senior',
    background: "Vétéran du diesel, anticipe les pannes avant qu'elles ne surviennent.",
    monthlyCost: 4500,
    prerequisite: { type: 'roleHired', value: 'role-mehdi-ferhat', label: 'Mehdi Ferhat recruté + 10+ véhicules' },
    extraPrerequisite: { type: 'vehicleCount', value: 10 },
    bonuses: [
      { type: 'maintenance_discount', value: 0.40, label: '-40 % coût maintenance' },
    ],
    unlocks: ['diag_preventif'],
  },

  // ===== QHSE / RSE =====
  // 01/06/2026 — Claire Maréchal centralises Quality, Health, Safety,
  // Environment AND CSR reporting. Her hire unlocks the RSE dashboard, the
  // annual EcoVadis audit, the RSE contract premium category, and HSE-specific
  // training compliance tracking. Without her, the player keeps a degraded
  // signal (raw reputation only) — none of the RSE/HSE specific mechanics
  // are exposed.
  {
    id: 'role-claire-marechal',
    familyId: 'qhse',
    role: 'Responsable QHSE / RSE',
    name: 'Claire Maréchal', age: 36,
    portrait: '/support-portraits/role-claire-marechal.webp',
    level: 'Senior',
    background:
      "Ingénieure HSE de formation, certifiée ISO 14001 et 45001. Pilote la responsabilité " +
      "sociétale (CO2, parité, salaires), les formations obligatoires FIMO/FCO et anticipe " +
      "les audits DREAL/URSSAF. Calme, méthodique, refuse les compromis sur la sécurité.",
    monthlyCost: 4100,
    prerequisite: { type: 'monthlyRevenue', value: 35000, label: 'CA > 35 000 €/mois + 4+ véhicules' },
    extraPrerequisite: { type: 'vehicleCount', value: 4 },
    bonuses: [
      { type: 'reputation_passive', value: 1, label: '+1 réputation / mois (gestion HSE)' },
      { type: 'rse_dashboard', value: 1, label: 'Débloque le tableau de bord RSE (score consolidé)' },
      { type: 'rse_premium_contracts', value: 1, label: 'Débloque les contrats RSE premium' },
    ],
    unlocks: ['rse_dashboard', 'rse_premium_contracts', 'hse_training_compliance'],
  },

  // ===== JURIDIQUE / CONSEIL =====
  // 17/06/2026 (Lot 6 Phase L1) — Maître Juliette Charron-Berthet,
  // avocate-conseil interne. Allège la gestion juridique : procédures
  // (URSSAF, prud'hommes, douanes, concurrence déloyale, DREAL), -30%
  // sur les amendes, débloque les options "Défense juridique" sur les
  // événements diplomatie (OPA, plainte) ET un bilan annuel juridique
  // signé. Alternative externe à la carte (cabinet d'avocats) — voir L2.
  {
    id: 'role-juliette-charron-berthet',
    familyId: 'juridique',
    role: 'Avocate-conseil interne',
    name: 'Maître Juliette Charron-Berthet', age: 42,
    portrait: '/support-portraits/role-juliette-charron-berthet.webp',
    level: 'Senior',
    background:
      "Avocate au Barreau de Paris, 15 ans d'expérience en droit des sociétés et droit " +
      "social. Ex-collaboratrice senior chez Gide Loyrette Nouel. Couvre l'ensemble du " +
      "périmètre : droit du travail, contentieux clients, concurrence déloyale, douanes " +
      "export. Pragmatique, négocie en interne plutôt qu'en procès quand c'est possible.",
    monthlyCost: 8200,
    prerequisite: { type: 'monthlyRevenue', value: 60000, label: 'CA > 60 000 €/mois + 6+ véhicules' },
    extraPrerequisite: { type: 'vehicleCount', value: 6 },
    bonuses: [
      { type: 'legal_amende_discount', value: 0.30, label: '-30 % sur les amendes et pénalités' },
      { type: 'legal_procedure_speed', value: 0.50, label: '-50 % temps de traitement des procédures' },
      { type: 'reputation_passive', value: 1, label: '+1 réputation / mois (image juridique solide)' },
    ],
    unlocks: ['juridique_defense', 'legal_dashboard', 'cabinet_avocat'],
  },
];

export const getSupportRolesByFamily = (familyId) =>
  SUPPORT_ROLES.filter(r => r.familyId === familyId);

export const getSupportRole = (id) =>
  SUPPORT_ROLES.find(r => r.id === id);

const checkSingle = (type, value, state) => {
  switch (type) {
    case 'monthlyRevenue':
      // 07/02/2026 — P1 retour user : critère CA basé sur max(M, M-1) au
      // lieu du seul mois courant. Évite qu'un mois faible (vacances,
      // démarrage de cycle) bloque arbitrairement un recrutement.
      // Si pas d'historique M-1, on retombe sur le mois courant.
      {
        const current = state.monthlyRevenues || 0;
        const history = Array.isArray(state.monthlyHistory) ? state.monthlyHistory : [];
        const prev = history.length > 0 ? (history[history.length - 1]?.revenues || 0) : 0;
        return Math.max(current, prev) >= value;
      }
    case 'driverCount':    return (state.drivers || []).length >= value;
    case 'vehicleCount':   return (state.vehicles || []).length >= value;
    case 'totalDays':      return (state.totalDays || 0) >= value;
    case 'treasury':       return (state.treasury || 0) >= value;
    case 'roleHired':      return !!(state.hiredSupport || {})[value];
    default: return true;
  }
};

/**
 * Evaluate whether the player meets a role's prerequisite (handles the
 * optional extraPrerequisite combined via AND).
 */
export const evaluatePrerequisite = (role, state) => {
  if (!role) return { ok: true, label: '' };
  const p = role.prerequisite;
  if (!p) return { ok: true, label: '' };
  const main = checkSingle(p.type, p.value, state);
  if (!role.extraPrerequisite) {
    return { ok: main, label: p.label };
  }
  const e = role.extraPrerequisite;
  const extra = checkSingle(e.type, e.value, state);
  return { ok: main && extra, label: p.label };
};
